import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { SpinnerService } from '../spinner.service';
import { BasicFolder } from '@r1-compass-library/models';
import { HttpHeaderConstants } from '@r1-compass-library/constants';
import { Observable, catchError, finalize, of, retry } from 'rxjs';
import { BasicFolderSearch } from '@r1-compass-library/models/search/basic-folder-search.model';
import { inject, Injectable } from '@angular/core';
import { AdvancedSearch } from '@r1-compass-library/models/search/advanced-search.model';

@Injectable({ providedIn: 'root' })
export abstract class FolderRestService<T extends BasicFolder, S extends BasicFolderSearch> {
    protected readonly http: HttpClient = inject(HttpClient);
    protected readonly spinnerService: SpinnerService = inject(SpinnerService);

    protected abstract BASE_URL: string;
    protected abstract TARGET_OBJECT: string;
    abstract processNamePrefix: string;

    search(search: S, additionalHeaders: any = {}): Observable<T[]> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams(),
        };

        this.setSearchParams(search, httpOptions);

        const process = `${this.processNamePrefix}.search`;
        this.spinnerService.addProcess(process, `Searching ${this.TARGET_OBJECT}s...`);
        return this.http.get<T[]>(`${this.BASE_URL}`, httpOptions).pipe(
            retry(0),
            catchError(() => of<T[]>()),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    get(id: number, additionalHeaders: any = {}): Observable<T> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        const process = `${this.processNamePrefix}.get`;
        this.spinnerService.addProcess(process, `Getting ${this.TARGET_OBJECT}...`);
        return this.http.get<T>(`${this.BASE_URL}/${id}`, httpOptions).pipe(
            retry(0),
            catchError(() => of<T>()),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    create(item: Partial<T>, additionalHeaders: any = {}): Observable<T> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        const process = `${this.processNamePrefix}.create`;
        this.spinnerService.addProcess(process, `Creating ${this.TARGET_OBJECT}...`);
        return this.http.post<T>(`${this.BASE_URL}`, item, httpOptions).pipe(
            retry(0),
            catchError(() => of<T>()),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    update(id: number, item: Partial<T>, additionalHeaders: any = {}): Observable<T> {
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        const process = `${this.processNamePrefix}.update`;
        this.spinnerService.addProcess(process, `Updating ${this.TARGET_OBJECT}...`);
        return this.http.patch<T>(`${this.BASE_URL}/${id}`, item, httpOptions).pipe(
            catchError(() => of<T>()),
            finalize(() => this.spinnerService.completeProcess(process))
        );
    }

    protected setSearchParams(search: S, httpOptions: { headers: HttpHeaders; params: HttpParams }) {
        for (const [key, value] of Object.entries(search)) {
            if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string') {
                httpOptions.params = httpOptions.params.set(key, value);
            } else if (Array.isArray(value)) {
                this.addArrayParams(key, value, httpOptions);
            } else {
                console.log('Search type is not yet supported. Type: ' + typeof value);
            }
        }
    }

    private addArrayParams(key: string, value: any[], httpOptions: any) {
        if (key === 'advancedSearch') {
            this.addAdvancedSearchParams(value, httpOptions);
        } else {
            value.forEach(val => {
                if (val !== undefined && val !== null) {
                    httpOptions.params = httpOptions.params.append(key, val);
                }
            });
        }
    }

    private addAdvancedSearchParams(advancedSearch: AdvancedSearch<BasicFolder>[], httpOptions: any): void {
        advancedSearch.forEach((searchCriteria, i) => {
            httpOptions.params = httpOptions.params.set(`advancedSearch[${i}].property`, searchCriteria.property);
            httpOptions.params = httpOptions.params.set(`advancedSearch[${i}].value`, searchCriteria.value);
            if (searchCriteria.operator) {
                httpOptions.params = httpOptions.params.set(`advancedSearch[${i}].operator`, searchCriteria.operator);
            }
        });
    }
}
